import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { increasePoiClicks } from '../actions/pois';
import wwwIcon from '../assets/icons/www_icon.png';
import COLORS from '../constants/colors';
import PoiRequestModal from './PoiRequestModal';

const i18nPrefix = 'FindMoorings';

const iconStyle = {
  width: 24,
  height: 24,
  strokeWidth: 1.5,
  fill: 'none',
  stroke: 'currentColor',
};

const ActionLink = ({ href, children, style, onClick }) => {
  if (!href) return null;

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <a
      href={href}
      target='_blank'
      rel='noreferrer'
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 12px',
        borderRadius: 14,
        background: 'rgba(255, 255, 255, 0.14)',
        color: COLORS.MAIN_WHITE,
        fontWeight: 600,
        textDecoration: 'none',
        fontSize: 13,
        transition: 'background 0.2s ease',
        flex: 1,
        ...style,
      }}>
      {children}
    </a>
  );
};

const formatUrl = (url) => {
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const urlRegex = /(https?:\/\/[^\s<]+)/g;

const linkifyText = (text) => {
  const parts = text.split(urlRegex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a
        key={i}
        href={part}
        target='_blank'
        rel='noreferrer'
        style={{
          color: '#7eb8ff',
          textDecoration: 'underline',
        }}>
        {part}
      </a>
    ) : (
      part
    ),
  );
};

const formatDuration = (seconds) => {
  if (seconds < 60 * 60) {
    return `~${Math.max(1, Math.round(seconds / 60))}m`;
  }

  if (seconds < 24 * 60 * 60) {
    return `~${Math.max(1, Math.round(seconds / (60 * 60)))}h`;
  }

  return `~${Math.max(1, Math.round(seconds / (24 * 60 * 60)))}d`;
};

function PoiModal({ show, poi, fromDate, toDate, onHide }) {
  const { t } = useTranslation();
  const [showPoiRequestModal, setShowPoiRequestModal] = useState(false);
  const descriptionText = poi?.text || poi?.description || '';

  const formattedDescription = useMemo(() => {
    if (!descriptionText) return [];
    return descriptionText.split('\n').filter((line) => line.trim().length > 0);
  }, [descriptionText]);

  const contacts = useMemo(() => {
    const url = `${process.env.REACT_APP_API_HOST}pois/forward_poi_link?poi_id=${poi.id}`;

    if (!poi) return [];
    return [
      {
        key: 'website',
        href: poi.website?.trim() ? formatUrl(url) : null,
        icon: <img src={wwwIcon} alt='website' width={24} height={24} />,
      },
      {
        key: 'phone',
        href: poi.phone ? `tel:${poi.phone}` : null,
        onClick: () => {
          increasePoiClicks(
            poi.id,
            'phone_counter',
            () => {},
            () => {},
          );
        },
        icon: (
          <svg {...iconStyle} viewBox='0 0 24 24'>
            <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.88 19.88 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.88 19.88 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2L8.09 9.91a16 16 0 0 0 6 6l1.38-1.38a2 2 0 0 1 2-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z' />
          </svg>
        ),
      },
      {
        key: 'email',
        href: poi.email ? `mailto:${poi.email}` : null,
        onClick: () => {
          increasePoiClicks(
            poi.id,
            'email_counter',
            () => {},
            () => {},
          );
        },
        icon: (
          <svg {...iconStyle} viewBox='0 0 24 24'>
            <path d='M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z' />
            <polyline points='22,6 12,13 2,6' />
          </svg>
        ),
      },
      {
        key: 'map_link',
        href: formatUrl(poi.map_link),
        onClick: () => {
          increasePoiClicks(
            poi.id,
            'maplink_counter',
            () => {},
            () => {},
          );
        },
        icon: (
          <svg {...iconStyle} viewBox='0 0 24 24'>
            <path d='M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z' />
            <circle cx='12' cy='10' r='3' />
          </svg>
        ),
      },
    ].filter((item) => item.href);
  }, [poi]);

  const periodLabel = useMemo(() => {
    if (!fromDate || !toDate) return null;
    const from = moment(fromDate);
    const to = moment(toDate);
    if (!from.isValid() || !to.isValid()) return null;
    return `${from.format('DD.MM.YYYY')} - ${to.format('DD.MM.YYYY')}`;
  }, [fromDate, toDate]);

  const imageUrl = poi?.image_url || poi?.picture_url || poi?.cover?.url;
  const email = t('global.boatparkContactEmail');
  const subject = t(`${i18nPrefix}.reportPoiMailSubject`, {
    title: poi?.title,
    id: poi?.id,
  });

  const account_email = localStorage.getItem('email');
  const body = t(`${i18nPrefix}.reportPoiMailBody`, {
    title: poi?.title,
    email: account_email || '',
  });
  const reportPoiHref = email
    ? `mailto:${email}?subject=${encodeURIComponent(
        subject,
      )}&body=${encodeURIComponent(body)}`
    : null;
  const canRequestPoiBooking = Boolean(
    poi?.email && poi.email.trim() !== '' && poi.allow_requests,
  );
  const replyQuality = Number.isFinite(poi?.reply_quality)
    ? Math.min(5, Math.max(1, poi.reply_quality))
    : null;
  const avgResponseTime = Number.isFinite(poi?.avg_response_time_seconds)
    && poi.avg_response_time_seconds > 0
    ? formatDuration(poi.avg_response_time_seconds)
    : null;

  if (!show || !poi) return null;

  return (
    <div
      style={{
        background: '#003066',
        borderRadius: 16,
        padding: 20,
        color: COLORS.MAIN_WHITE,
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.25)',
        maxWidth: 420,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
      }}>
      <button
        type='button'
        onClick={onHide}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(255, 255, 255, 0.15)',
          color: COLORS.MAIN_WHITE,
          fontWeight: 700,
          fontSize: 16,
          cursor: 'pointer',
        }}>
        ×
      </button>

      {imageUrl && (
        <img
          src={imageUrl}
          alt={poi?.title || 'Point of Interest'}
          style={{
            width: '100%',
            borderRadius: 12,
            objectFit: 'cover',
            height: 160,
            marginBottom: 16,
          }}
        />
      )}

      <div style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 8 }}>
        {poi?.title || 'Point of Interest'}
      </div>

      {Boolean(poi?.allow_requests) && replyQuality && (
        <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
          {replyQuality && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, opacity: 0.8 }}>
                {t(`${i18nPrefix}.replyQuality`, {
                  defaultValue: 'Reply quality',
                })}
              </span>
              <span
                aria-label={`${replyQuality} out of 5`}
                style={{ display: 'flex', gap: 2, color: COLORS.MAIN_GREEN }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    width='18'
                    height='18'
                    viewBox='0 0 24 24'
                    fill={star <= replyQuality ? 'currentColor' : 'none'}
                    stroke='currentColor'
                    strokeWidth='1.8'
                    aria-hidden='true'>
                    <path d='m12 2 3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14l-5-4.87 6.91-1.01L12 2z' />
                  </svg>
                ))}
              </span>
            </div>
          )}
          {avgResponseTime && (
            <div style={{ fontSize: 14, opacity: 0.8 }}>
              {t(`${i18nPrefix}.avgResponseTime`, {
                defaultValue: 'Avg. response time',
              })}
              {`: ${avgResponseTime}`}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          height: 1,
          background: 'rgba(255, 255, 255, 0.2)',
          marginBottom: 12,
          width: '100%',
        }}
      />

      {poi?.address && (
        <div style={{ fontSize: 14, marginBottom: 8, opacity: 0.8 }}>
          {poi.address}
        </div>
      )}

      {formattedDescription.length > 0 && (
        <div
          style={{
            lineHeight: 1.2,
            fontSize: 14,
            display: 'grid',
            gap: 6,
            marginBottom: 12,
          }}>
          {formattedDescription.map((line, index) => (
            <span key={`poi-description-${index}`}>{linkifyText(line)}</span>
          ))}
        </div>
      )}

      {contacts.length > 0 && (
        <>
          {(poi?.address || formattedDescription.length > 0) && (
            <div
              style={{
                height: 1,
                background: 'rgba(255, 255, 255, 0.2)',
                marginBottom: 12,
                width: '100%',
              }}
            />
          )}
          <div
            style={{
              display: 'flex',
              gap: 8,
              width: '100%',
            }}>
            {contacts.map((contact) => (
              <ActionLink
                key={contact.key}
                href={contact.href}
                onClick={contact.onClick}>
                {contact.icon}
              </ActionLink>
            ))}
          </div>
        </>
      )}

      {(canRequestPoiBooking || reportPoiHref) && (
        <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
          {canRequestPoiBooking && (
            <button
              type='button'
              onClick={() => setShowPoiRequestModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 14,
                background: COLORS.MAIN_GREEN,
                color: COLORS.MAIN_WHITE,
                fontWeight: 600,
                textDecoration: 'none',
                fontSize: 13,
                border: 'none',
                cursor: 'pointer',
              }}>
              <span>{t(`${i18nPrefix}.poiBookingRequest`)}</span>
            </button>
          )}
          {reportPoiHref && (
            <ActionLink
              href={reportPoiHref}
              style={{
                background: 'none',
                padding: 0,
                color: COLORS.LINE_GRAY,
              }}>
              <svg {...iconStyle} viewBox='0 0 24 24'>
                <path d='M12 3 2 21h20L12 3z' />
                <path d='M12 9v5' />
                <circle cx='12' cy='17' r='1' />
              </svg>
              <span>{t(`${i18nPrefix}.reportPoi`)}</span>
            </ActionLink>
          )}
        </div>
      )}
      <PoiRequestModal
        show={showPoiRequestModal}
        onHide={() => setShowPoiRequestModal(false)}
        poi={poi}
        fromDate={fromDate}
        toDate={toDate}
      />
    </div>
  );
}

export default PoiModal;
